// v2 外层页面（v2 spec §4）：首页、城市选择、每日挑战、图鉴、设置与统计。
// 全部是 DOM 叠层（文字是 DOM），底图复用 public/ui 的卡纸 / 毛毡零件；画布在这些页面上只清屏。
import { formatTime } from './format';
import { gridLabel, type Grid, type LevelMods } from '../core/levels';
import { modLabels, starsEl } from './stars';
import { runeSvg } from './runeIcons';

export type HomeModel = {
  continueLabel: string;
  cover: string | null;
  coverTitle: string;
  daily: { done: boolean; streak: number };
  /** 月亮塔罗的独立入口（没有塔罗主题时为 null） */
  tarot: { title: string; cover: string; lit: number; total: number; runes: number; runeTotal: number } | null;
};

export type CityModel = {
  id: string;
  title: string;
  subtitle: string;
  cover: string;
  /** 封面是竖版（塔罗牌面）：缩略图按 2:3 显示 */
  portrait?: boolean;
  completed: number;
  total: number;
  open: boolean;
  need: number;
  /** 前一城的名字（锁着时说明用） */
  prevTitle: string | null;
};

export type CalendarCell = { date: string; day: number; done: boolean; today: boolean; future: boolean };

export type DailyModel = {
  date: string;
  placeTitle: string;
  themeTitle: string;
  image: string;
  n: number;
  /** 实际的格（抽到竖版塔罗牌时是竖版格） */
  grid?: Grid;
  mods: LevelMods;
  result: { timeMs: number; moves: number; stars: number | null } | null;
  hasProgress: boolean;
  streak: number;
  bestStreak: number;
  calendar: CalendarCell[];
};

export type AlbumCell = {
  /** 所在章节标题（有章节的主题，如塔罗）；换章时在格子前插一行章节标题 */
  chapter?: string | null;
  themeId: string;
  placeId: string;
  title: string;
  state: 'completed' | 'unlocked' | 'locked';
  image: string;
  teaser: string;
  catFound: boolean;
  /** 最好星级（0 = 没有） */
  stars: number;
};

export type AlbumModel = {
  tabs: { id: string; title: string }[];
  tab: string;
  /** 这个标签页的图是竖版（塔罗）：缩略图保持 2:3 */
  portrait?: boolean;
  cells: AlbumCell[];
  books: { done: number; total: number };
  cats: { found: number; total: number };
  stars: { got: number; total: number };
  /** 有符文的主题（塔罗）：一排符文图标，没找到的变暗 */
  runes?: { icon: string; name: string; found: boolean }[];
  /** 有星盘的主题：《月亮之书》各页；没解开的模糊显示 + 「解开第 N 章星盘」 */
  book?: { title: string; pages: { title: string; text: string; open: boolean; need: string }[] };
};

export type SettingsModel = {
  haptics: boolean;
  gyro: boolean;
  version: string;
  stats: {
    puzzlesCompleted: number;
    totalPlayMs: number;
    cats: { found: number; total: number };
    stars: { got: number; total: number };
    streak: number;
    bestStreak: number;
    /** key = 成绩键（'6' / '7x10'），label = 显示（6×6 / 7×10） */
    best: { key: string; label: string; timeMs: number | null; moves: number | null }[];
  };
};

export type PagesActions = {
  continue: () => void;
  journey: () => void;
  tarot: () => void;
  daily: () => void;
  album: () => void;
  settings: () => void;
  openCity: (id: string) => void;
  lockedCity: (id: string) => void;
  playDaily: () => void;
  albumTab: (id: string) => void;
  albumOpen: (themeId: string, placeId: string) => void;
  setHaptics: (on: boolean) => void;
  setGyro: (on: boolean) => void;
  resetSave: () => void;
};

export type PageView = 'home' | 'cities' | 'daily' | 'album' | 'settings' | 'dial';

const el = <K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string, text?: string): HTMLElementTagNameMap[K] => {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text !== undefined) e.textContent = text;
  return e;
};

const img = (src: string, cls: string, alt = '') => {
  const i = el('img', cls);
  i.src = src;
  i.alt = alt;
  i.draggable = false;
  i.decoding = 'async';
  return i;
};

function feltButton(id: string, color: 'yellow' | 'blue' | 'red', label: string, sub?: string, icon?: string): HTMLButtonElement {
  const b = el('button', `ui-btn ui-btn--wide ui-btn--${color} page-btn`);
  b.type = 'button';
  b.id = id;
  if (icon) b.append(img(icon, 'page-btn-icon'));
  const text = el('span', 'page-btn-text');
  text.append(el('span', 'page-btn-label', label));
  if (sub) text.append(el('span', 'page-btn-sub', sub));
  b.append(text);
  return b;
}

const WEEKDAYS = ['一', '二', '三', '四', '五', '六', '日'];

function dateLabel(date: string): string {
  const [, m, d] = date.split('-').map(Number);
  return `${m}月${d}日`;
}

export class Pages {
  private readonly root: HTMLElement;
  view: PageView | null = null;

  constructor(private readonly a: PagesActions) {
    this.root = document.querySelector<HTMLElement>('#pages')!;
  }

  hide(): void {
    this.root.hidden = true;
    this.root.replaceChildren();
    this.view = null;
  }

  mount(view: PageView, ...children: HTMLElement[]): void {
    this.view = view;
    this.root.dataset.view = view;
    const inner = el('div', `page page--${view}`);
    inner.append(...children);
    this.root.replaceChildren(inner);
    this.root.hidden = false;
    this.root.scrollTop = 0;
  }

  // ── 首页 ──

  showHome(m: HomeModel): void {
    const hero = el('figure', 'home-hero');
    if (m.cover) hero.append(img(m.cover, 'home-cover'));
    hero.append(el('figcaption', 'home-hero-caption', m.coverTitle));
    const list = el('nav', 'home-buttons');
    const cont = feltButton('home-continue', 'yellow', '继续', m.continueLabel);
    cont.onclick = this.a.continue;
    const journey = feltButton('home-journey', 'blue', '旅程', '巴黎 → 京都 → 北欧');
    journey.onclick = this.a.journey;
    let tarot: HTMLButtonElement | null = null;
    if (m.tarot) {
      // 月亮塔罗：独立的大按钮（夜空蓝 + 金线），左边一张小牌面
      tarot = el('button', 'home-tarot');
      tarot.type = 'button';
      tarot.id = 'home-tarot';
      const pic = el('span', 'home-tarot-card');
      pic.append(img(m.tarot.cover, 'home-tarot-img'));
      const text = el('span', 'home-tarot-text');
      text.append(el('span', 'home-tarot-title', m.tarot.title), el('span', 'home-tarot-sub', `已点亮 ${m.tarot.lit}/${m.tarot.total} · 符文 ${m.tarot.runes}/${m.tarot.runeTotal}`));
      const moon = el('span', 'home-tarot-moon');
      moon.append(runeSvg('crescent', 'home-tarot-glyph'));
      tarot.append(pic, text, moon);
      tarot.onclick = this.a.tarot;
    }
    const dailySub = `${m.daily.done ? '今日已完成 ✓' : '今日未完成'} · 连续 ${m.daily.streak} 天`;
    const daily = feltButton('home-daily', 'red', '每日挑战', dailySub);
    daily.dataset.done = String(m.daily.done);
    daily.onclick = this.a.daily;
    const row = el('div', 'home-row');
    const album = feltButton('home-album', 'blue', '图鉴', undefined, '/ui/icons/book.png');
    album.onclick = this.a.album;
    const settings = feltButton('home-settings', 'blue', '设置');
    settings.onclick = this.a.settings;
    row.append(album, settings);
    list.append(cont, journey, ...(tarot ? [tarot] : []), daily, row);
    this.mount('home', hero, list);
  }

  // ── 城市选择 ──

  showCities(cities: CityModel[]): void {
    const list = el('div', 'city-list');
    for (const c of cities) {
      const card = el('button', `ui-card city-card${c.open ? '' : ' is-locked'}`);
      card.type = 'button';
      card.dataset.theme = c.id;
      card.setAttribute('aria-disabled', String(!c.open));
      const thumb = el('div', `city-thumb${c.portrait ? ' is-portrait' : ''}`);
      thumb.append(img(c.cover, 'city-cover'));
      if (!c.open) thumb.append(img('/ui/icons/lock.png', 'city-lock'));
      const body = el('div', 'city-body');
      body.append(el('h2', 'city-title', c.title), el('p', 'city-sub', c.subtitle));
      if (c.open) body.append(el('p', 'city-progress', `已点亮 ${c.completed}/${c.total}`));
      else body.append(el('p', 'city-need', `${c.prevTitle ? `在${c.prevTitle}` : ''}再完成 ${c.need} 个地点开放`));
      card.append(thumb, body);
      card.onclick = () => (c.open ? this.a.openCity(c.id) : this.a.lockedCity(c.id));
      list.append(card);
    }
    this.mount('cities', list);
  }

  shakeCity(id: string): void {
    const c = this.root.querySelector<HTMLElement>(`.city-card[data-theme="${id}"]`);
    if (!c) return;
    c.classList.remove('is-shake');
    void c.offsetWidth;
    c.classList.add('is-shake');
  }

  // ── 每日挑战 ──

  showDaily(m: DailyModel): void {
    const card = el('section', 'ui-card daily-card');
    const head = el('p', 'daily-date', `${dateLabel(m.date)} · ${gridLabel(m.grid ?? { cols: m.n, rows: m.n })}`);
    for (const tag of modLabels(m.mods)) {
      const t = el('span', `card-mod is-${tag.kind}`);
      t.dataset.mod = tag.kind;
      t.append(el('i', 'card-mod-icon'), el('span', '', tag.text));
      head.append(t);
    }
    card.append(head);
    const portraitPic = !!m.grid && m.grid.rows !== m.grid.cols;
    const pic = el('div', `daily-pic${portraitPic ? ' is-portrait' : ''}`);
    pic.append(img(m.image, 'daily-img'));
    card.append(pic, el('h2', 'daily-title', m.placeTitle), el('p', 'daily-theme', m.themeTitle));
    const status = el('p', `daily-status${m.result ? ' is-done' : ''}`);
    status.id = 'daily-status';
    if (m.result) {
      status.append(el('span', '', '今日成绩 '), el('b', 'daily-time', formatTime(m.result.timeMs)), el('span', '', ' · '), el('b', 'daily-moves', String(m.result.moves)), el('span', '', ' 步'));
      if (m.result.stars) status.append(starsEl(m.result.stars, 'daily-stars'));
    } else status.textContent = '今天还没完成';
    card.append(status);
    const label = m.hasProgress ? '继续' : m.result ? '再玩一次' : '开始';
    const play = feltButton('daily-play', 'yellow', label, m.result ? '重玩不计成绩' : undefined);
    play.onclick = this.a.playDaily;
    const actions = el('div', 'card-actions');
    actions.append(play);
    card.append(actions);

    const cal = el('section', 'ui-card daily-cal');
    cal.append(el('p', 'daily-streak', `连续 ${m.streak} 天 · 最长 ${m.bestStreak} 天`));
    const grid = el('div', 'cal-grid');
    grid.setAttribute('role', 'grid');
    for (const w of WEEKDAYS) grid.append(el('span', 'cal-head', w));
    for (const c of m.calendar) {
      const cell = el('span', `cal-cell${c.done ? ' is-done' : ''}${c.today ? ' is-today' : ''}${c.future ? ' is-future' : ''}`, String(c.day));
      cell.dataset.date = c.date;
      cell.setAttribute('aria-label', `${dateLabel(c.date)}${c.done ? ' 已完成' : ''}`);
      grid.append(cell);
    }
    cal.append(grid);
    this.mount('daily', card, cal);
  }

  // ── 图鉴 ──

  showAlbum(m: AlbumModel): void {
    const tabs = el('div', 'album-tabs');
    tabs.setAttribute('role', 'tablist');
    for (const t of m.tabs) {
      const b = el('button', `ui-chip album-tab${t.id === m.tab ? ' is-on' : ''}`, t.title);
      b.type = 'button';
      b.dataset.theme = t.id;
      b.setAttribute('role', 'tab');
      b.setAttribute('aria-selected', String(t.id === m.tab));
      b.onclick = () => this.a.albumTab(t.id);
      tabs.append(b);
    }
    const stats = el('p', 'album-stats');
    stats.append(
      el('span', '', '立体书 '), el('b', 'album-books', `${m.books.done}/${m.books.total}`),
      el('span', '', ' · 猫 '), el('b', 'album-cats', `${m.cats.found}/${m.cats.total}`),
      el('span', '', ' · 星 '), el('b', 'album-stars', `${m.stars.got}/${m.stars.total}`),
    );
    if (m.runes) stats.append(el('span', '', ' · 符文 '), el('b', 'album-runes', `${m.runes.filter((r) => r.found).length}/${m.runes.length}`));
    const extra: HTMLElement[] = [];
    if (m.runes) {
      const sec = el('section', 'album-runes-row');
      sec.append(el('h3', 'album-chapter', '符文'));
      const row = el('div', 'rune-strip');
      for (const r of m.runes) {
        const cell = el('span', `rune-chip${r.found ? ' is-found' : ''}`);
        cell.title = r.found ? r.name : '？';
        cell.setAttribute('aria-label', r.found ? `符文 ${r.name}` : '符文：还没找到');
        cell.append(runeSvg(r.icon, 'rune-chip-icon'));
        row.append(cell);
      }
      sec.append(row);
      extra.push(sec);
    }
    if (m.book) {
      const sec = el('section', 'album-book');
      sec.append(el('h3', 'album-chapter', `《${m.book.title}》`));
      m.book.pages.forEach((pg, i) => {
        const page = el('article', `ui-card book-page${pg.open ? ' is-open' : ' is-locked'}`);
        page.dataset.page = String(i);
        page.append(el('h4', 'book-page-title', pg.open ? pg.title : `第${['一', '二', '三', '四', '五'][i] ?? i + 1}页`));
        const body = el('p', 'book-page-text', pg.open ? pg.text : '月光把这一页的字藏了起来，只看得见一些模糊的笔画，像星星在水里摇晃。');
        page.append(body);
        if (!pg.open) page.append(el('span', 'book-page-need', pg.need));
        sec.append(page);
      });
      extra.push(sec);
    }
    const grid = el('div', `album-grid${m.portrait ? ' is-portrait' : ''}`);
    let chapter: string | null = null;
    for (const c of m.cells) {
      if (c.chapter && c.chapter !== chapter) {
        chapter = c.chapter;
        const head = el('h3', 'album-chapter', c.chapter);
        grid.append(head);
      }
      const cell = el('button', `album-cell is-${c.state}`);
      cell.type = 'button';
      cell.dataset.place = c.placeId;
      cell.dataset.state = c.state;
      const thumb = el('div', 'album-thumb');
      const text = el('div', 'album-text');
      if (c.state === 'completed') {
        thumb.append(img(c.image, 'album-img'));
        text.append(el('b', 'album-title', c.title));
        // 有星级记录才显示（星级上线前通关的地点没有记录，不显示成 0 星）
        if (c.stars > 0) text.append(starsEl(c.stars, 'album-stars-row'));
        text.append(el('span', `album-cat${c.catFound ? ' is-found' : ''}`, c.catFound ? '猫 ● 找到了' : '猫 ○ 没找到'));
        cell.onclick = () => this.a.albumOpen(c.themeId, c.placeId);
        cell.setAttribute('aria-label', `${c.title}，回看立体书`);
      } else if (c.state === 'unlocked') {
        thumb.append(img(c.image, 'album-sil'));
        text.append(el('b', 'album-title', '？？？'), el('span', 'album-teaser', c.teaser));
        cell.disabled = true;
      } else {
        thumb.append(img('/ui/icons/lock.png', 'album-lock'));
        text.append(el('b', 'album-title', '未解锁'));
        cell.disabled = true;
      }
      cell.append(thumb, text);
      grid.append(cell);
    }
    this.mount('album', tabs, stats, ...extra, grid);
  }

  // ── 设置与统计 ──

  showSettings(m: SettingsModel): void {
    const card = el('section', 'ui-card settings-card');
    const toggle = (id: string, label: string, note: string, on: boolean, set: (v: boolean) => void) => {
      const row = el('div', 'set-row');
      const t = el('div', 'set-label');
      t.append(el('b', '', label), el('span', 'set-note', note));
      const b = el('button', `ui-chip set-toggle${on ? ' is-on' : ''}`, on ? '开' : '关');
      b.type = 'button';
      b.id = id;
      b.setAttribute('role', 'switch');
      b.setAttribute('aria-checked', String(on));
      b.setAttribute('aria-label', label);
      b.onclick = () => set(!on);
      row.append(t, b);
      return row;
    };
    card.append(
      toggle('set-haptics', '震动', '点按与拼错时的轻震', m.haptics, this.a.setHaptics),
      toggle('set-gyro', '陀螺仪倾斜', m.gyro ? '倾斜手机观看立体书' : '已关闭：用手指拖动倾斜', m.gyro, this.a.setGyro),
    );
    const resetRow = el('div', 'set-row');
    const rl = el('div', 'set-label');
    rl.append(el('b', '', '重置存档'), el('span', 'set-note', '清空全部进度、成绩与打卡'));
    const reset = el('button', 'ui-btn ui-btn--wide ui-btn--red set-reset');
    reset.type = 'button';
    reset.id = 'set-reset';
    reset.append(el('span', '', '重置'));
    reset.onclick = this.a.resetSave;
    resetRow.append(rl, reset);
    card.append(resetRow);

    const s = m.stats;
    const stats = el('section', 'ui-card stats-card');
    stats.append(el('h2', 'stats-title', '统计'));
    const dl = el('dl', 'stats-list');
    const item = (k: string, v: string, id: string) => {
      const dt = el('dt', '', k);
      const dd = el('dd', '', v);
      dd.id = id;
      dl.append(dt, dd);
    };
    item('完成拼图', `${s.puzzlesCompleted} 次`, 'stat-puzzles');
    item('总用时', formatDuration(s.totalPlayMs), 'stat-time');
    item('找到的猫', `${s.cats.found}/${s.cats.total}`, 'stat-cats');
    item('星星', `${s.stars.got}/${s.stars.total}`, 'stat-stars');
    item('每日连续', `当前 ${s.streak} 天 · 最长 ${s.bestStreak} 天`, 'stat-streak');
    stats.append(dl);
    const table = el('table', 'stats-best');
    const head = el('tr');
    head.append(el('th', '', '难度'), el('th', '', '最短用时'), el('th', '', '最少步数'));
    table.append(head);
    for (const b of s.best) {
      const tr = el('tr');
      tr.dataset.n = b.key;
      tr.append(el('td', '', b.label), el('td', '', b.timeMs === null ? '—' : formatTime(b.timeMs)), el('td', '', b.moves === null ? '—' : String(b.moves)));
      table.append(tr);
    }
    stats.append(table);
    const ver = el('p', 'set-version', `版本 ${m.version}`);
    ver.id = 'set-version';
    this.mount('settings', card, stats, ver);
  }
}

/** 总用时：1 小时 2 分 / 3 分 4 秒 */
export function formatDuration(ms: number): string {
  const s = Math.floor(Math.max(0, ms) / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h > 0) return `${h} 小时 ${m} 分`;
  return `${m} 分 ${s % 60} 秒`;
}
